import { FileText, Image, FileSpreadsheet, Archive, Paperclip, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAttachments } from '@/hooks/useAttachments';
import { useDownloadAttachment } from '@/hooks/useDownloadAttachment';
import { formatFileSize } from '@/lib/formatFileSize';
import { Skeleton } from '@/components/ui/skeleton';

interface AttachmentListProps {
  emailId: string;
  emailAccountId: string;
  messageId: string;
}

const getFileIcon = (contentType: string) => {
  if (contentType.includes('pdf')) return FileText;
  if (contentType.startsWith('image/')) return Image;
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return FileSpreadsheet;
  if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('archive')) return Archive;
  return Paperclip;
};

export function AttachmentList({ emailId, emailAccountId, messageId }: AttachmentListProps) {
  const { data: attachments, isLoading } = useAttachments(emailId);
  const downloadAttachment = useDownloadAttachment();

  if (isLoading) {
    return (
      <div className="flex gap-2 flex-wrap">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-16 w-48" />
        ))}
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground">
        Attachments ({attachments.length})
      </h3>
      <div className="flex gap-2 flex-wrap">
        {attachments.map((attachment) => {
          const Icon = getFileIcon(attachment.content_type);
          const isDownloading = downloadAttachment.isPending;

          return (
            <Card key={attachment.id} className="p-3 flex items-center gap-3 max-w-xs">
              <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" title={attachment.filename}>
                  {attachment.filename}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(attachment.size_bytes)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  downloadAttachment.mutate({
                    emailAccountId,
                    messageId,
                    attachmentId: attachment.attachment_id,
                    filename: attachment.filename,
                  })
                }
                disabled={isDownloading}
                title="Download"
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
